import {
  IModalProps,
  MessageBar,
  MessageBarType,
  Modal,
  Stack,
  SubmissionForm,
  TextInputField,
  UseFormMethods,
  useTheme
} from "@bps/fluent-ui";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  ComparisonOperator,
  DeviceDetail,
  DeviceSearchSelectorCreateType
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useCreateSelector } from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { SelectorFormValues } from "../selectors/edit/formUtils";
import {
  selectorFormSchema,
  selectorFormValidator
} from "../selectors/edit/SelectorFormValidator";
import { SelectorCreateType } from "./DeviceSearchForm";

interface CreateSelectorModalProps extends IModalProps {
  show: boolean;
  onDismiss: () => void;
  selector: SelectorFormValues;
  selectedDevices: DeviceDetail[];
  createType: SelectorCreateType;
}

export const CreateSelectorModal: React.FC<CreateSelectorModalProps> = ({
  show,
  onDismiss,
  selector,
  selectedDevices,
  createType,
  ...props
}) => {
  const theme = useTheme();

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const { mutateAsync: createSelector, error } = useCreateSelector();

  const onSubmit = async (
    values: SelectorFormValues,
    actions: Pick<UseFormMethods<SelectorFormValues>, "reset" | "setValue">
  ) => {
    if (createType === DeviceSearchSelectorCreateType.FromCriteria) {
      const { rules, ...rest } = values;

      await createSelector({
        ...rest,
        rules: selector.rules
      });
    } else {
      // Create rules for each device
      const rules = selectedDevices.map(device => {
        return {
          attributeType: "Id",
          valueDataType: "STRING",
          comparisonOperator: ComparisonOperator.Equals,
          value: device.id
        };
      });

      await createSelector({
        ...values,
        rules
      });
    }

    // Reset form and navigate to previous page
    onDismiss();
    actions.reset(values);
  };

  const defaultValues: SelectorFormValues = {
    description: selector?.description ?? "",
    name: selector?.name ?? "",
    rules: []
  };

  return (
    <Modal
      isOpen={show}
      {...props}
      styles={{ scrollableContent: { maxHeight: "none" } }}
    >
      <Stack
        verticalFill
        styles={{
          root: { padding: theme.spacing.l1, maxHeight: 800 }
        }}
        tokens={{ childrenGap: theme.spacing.s1 }}
      >
        <SubmissionForm<SelectorFormValues>
          onSubmit={onSubmit}
          initialValues={defaultValues}
          submitButtonProps={{
            text: "Create"
          }}
          cancelButtonProps={{ onClick: onDismiss, text: "Cancel" }}
          disabled={!hasWritePermission}
          validate={values =>
            selectorFormValidator.validate(values, selectorFormSchema)
          }
        >
          {() => {
            return (
              <>
                {error && (
                  <MessageBar messageBarType={MessageBarType.error}>
                    {error?.detail as string}
                  </MessageBar>
                )}

                <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
                  <TextInputField
                    label="Selector Name"
                    required
                    name="name"
                    styles={{ root: { width: 250 } }}
                  />
                  <TextInputField
                    label="Description"
                    name="description"
                    styles={{ root: { width: 250 } }}
                  />
                </Stack>
              </>
            );
          }}
        </SubmissionForm>
      </Stack>
    </Modal>
  );
};
